import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { currentMembership, useHousehold, useMyBookings, type Person } from '@/lib/queries';
import { ageBand, date, dateTime, gradeLabel, money, unpaidText } from '@/lib/format';
import { Spinner, ErrorNote } from '@/components/Layout';
import { Seal } from '@/components/Brand';
import { PhotoUpload } from '@/components/Photo';
import { memberLinks } from '@/lib/links';
import { MemberCodeButtons } from '@/components/MemberCode';
import { Printer, ScanLine } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

function Status({ p, autoRenewsOn }: { p: Person; autoRenewsOn: string | null }) {
  const m = currentMembership(p);
  const today = new Date().toISOString().slice(0, 10);
  if (p.frozen_at) return <p className="text-[15px] font-semibold text-indigo-600">Membership paused</p>;
  if (!m) return <p className="text-[15px] font-semibold text-brand-700">Not a member yet</p>;
  if (m.status === 'active' && m.ends_on >= today) {
    const days = Math.round((new Date(m.ends_on).getTime() - Date.now()) / 86_400_000);
    if (autoRenewsOn && autoRenewsOn > today) return <p className="text-[15px]">Member until <strong>{date(m.ends_on)}</strong>, renews by Direct Debit</p>;
    return days <= 30
      ? <p className="text-[15px] font-semibold text-brand-700">Renew by {date(m.ends_on)}</p>
      : <p className="text-[15px]">Member until <strong>{date(m.ends_on)}</strong></p>;
  }
  if (m.status === 'pending') return <p className="text-[15px] text-indigo-600">Payment on its way</p>;
  return <p className="text-[15px] font-semibold text-brand-700">Membership ended {date(m.ends_on)}</p>;
}

export function Home() {
  const household = useHousehold();
  const bookings = useMyBookings(household.data?.householdId);
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['household'] });
  // A search result links to #p-<person>: scroll to that card once the household has loaded.
  const { hash } = useLocation();
  useEffect(() => { if (household.data && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }); }, [household.data, hash]);
  if (household.isLoading) return <Spinner />;
  if (household.error) return <ErrorNote>We couldn't load your household. Please refresh the page.</ErrorNote>;
  if (!household.data) return <Navigate to="/join" replace />;
  const { people, autoRenewsOn } = household.data;
  const frozen = people.filter(p => p.frozen_at);
  const unpaid = people.filter(p => !p.frozen_at).filter(p => { const m = currentMembership(p); return !m || m.status !== 'active' || m.ends_on < new Date().toISOString().slice(0, 10); });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">My household</h1>
        <p className="mt-1 text-sm text-ink-500">Everyone you look after, and where their membership stands.</p>
      </div>

      {params.get('booked') && <div className="card border-ink-900 text-[15px]">You're booked in. We've emailed you a confirmation.{params.get('booked') === 'otd' && ' Please pay your instructor or at the door on the day.'}</div>}
      {params.get('entered') && <div className="card border-ink-900 text-[15px]">Grading entry made. We've asked your sensei to approve it, and we'll email you their decision.</div>}

      {frozen.map(p => (
        <div key={p.id} className="card border-indigo-600 text-[15px]" role="status">
          <p><strong>{p.first_name}'s membership is paused.</strong>{p.frozen_reason && <> {p.frozen_reason}</>}</p>
          <p className="mt-1 text-ink-500">Until it is restored {p.first_name} cannot book events or enter gradings. Please contact the Membership Secretary at <a className="link" href="mailto:membership.secretary@egka.org.uk">membership.secretary@egka.org.uk</a>.</p>
        </div>
      ))}

      {unpaid.length > 0 && (
        <div className="card flex flex-wrap items-center justify-between gap-3 border-brand-200">
          <p className="text-[15px] text-ink-900"><strong>{unpaid.length === 1 ? `${unpaid[0]!.first_name} needs` : `${unpaid.length} people need`}</strong> a membership to train and to book events.</p>
          <Link to="/renew" className="btn-primary">Choose memberships</Link>
        </div>
      )}

      {unpaid.length === 0 && (
        <div className="flex justify-end"><Link to="/renew" className="btn-ghost border border-stone-300">Renew early</Link></div>
      )}

      <DeskLinks />
      {people.length > 1 && <div className="flex justify-end"><Link to={`/cards?ids=${people.map(p => p.id).join(',')}`} className="btn-small"><Printer size={13} /> Print everyone's cards</Link></div>}
      <ul className="grid gap-3 sm:grid-cols-2">
        {people.map(p => (
          <li key={p.id} id={`p-${p.id}`} className="card scroll-mt-4 target:ring-2 target:ring-brand-200">
            <div className="flex items-start gap-4">
              <PhotoUpload personId={p.id} path={p.photo_path} name={`${p.first_name} ${p.last_name}`} onChanged={refresh} size={56} />
              <div className="min-w-0 flex-1">
                <p className="text-lg font-bold"><Link to={memberLinks.person(p.id)} className="hover:underline">{p.first_name} {p.last_name}</Link></p>
                <p className="text-sm text-ink-500">
                  {gradeLabel(p.grade_rank)} · {p.dojo ? <Link to={`/dojos#dojo-${p.dojo.id}`} className="link">{p.dojo.short_name ?? p.dojo.name}</Link> : 'No dojo chosen'}
                  {ageBand(p.date_of_birth) && <> · <span className="capitalize">{ageBand(p.date_of_birth)}</span></>}
                </p>
              </div>
              {(() => { const m = currentMembership(p); return !p.frozen_at && m && m.status === 'active' && m.ends_on >= new Date().toISOString().slice(0, 10) ? <Seal size={46} /> : null; })()}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              {p.member_number ? <p className="text-xs text-ink-500">EGKA member no. {p.member_number}</p> : <span />}
              <MemberCodeButtons person={p} />
            </div>
            {ageBand(p.date_of_birth) !== null && ageBand(p.date_of_birth) !== 'adult' && (
              <PhotoConsent personId={p.id} firstName={p.first_name} value={p.photo_consent} onSaved={refresh} />
            )}
            <div className="mt-3 flex items-center justify-between gap-2"><Status p={p} autoRenewsOn={autoRenewsOn} />{p.is_household_manager && <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Account holder</span>}</div>
          </li>
        ))}
      </ul>

      <GradingHistory people={people} onChange={refresh} />

      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl">Bookings</h2>
          <Link to="/events" className="text-sm link">See events</Link>
        </div>
        {!bookings.data?.length ? (
          <p className="card mt-3 text-sm text-ink-500">No upcoming bookings.</p>
        ) : (
          <ul className="card mt-3 divide-y divide-stone-100 p-0">
            {bookings.data.map(b => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                <span><strong>{b.person ? <Link to={memberLinks.person(b.person.id)} className="hover:underline">{b.person.first_name}</Link> : null}</strong> · {b.event ? <Link to={`/events/${b.event.slug}`} className="link">{b.event.title}</Link> : null} · {b.option?.name}</span>
                <span className="text-ink-500">{b.event && dateTime(b.event.starts_at)} · {b.status === 'pending' ? (b.pay_on_day ? unpaidText(b, b.event?.starts_at) : 'Payment pending') : b.price_pence ? money(b.price_pence) : 'Free'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const RESULT: Record<string, string> = { passed: 'Passed', failed: 'Not this time', registered: 'Entered', withdrawn: 'Withdrawn' };
const status = (g: Person['gradings'][number]) => g.result !== 'registered' ? RESULT[g.result]
  : g.recommended === null ? "Awaiting sensei's approval" : g.recommended ? `Approved by ${g.recommended_by ?? 'sensei'}` : 'Not approved this time';

/** Each person's gradings, newest first. Results are recorded by EGKA after each grading. */
function GradingHistory({ people, onChange }: { people: Person[]; onChange: () => void }) {
  const rows = people.flatMap(p => (p.gradings ?? []).filter(g => g.result !== 'withdrawn').map(g => ({ ...g, who: p.first_name, whoId: p.id })))
    .sort((a, b) => b.graded_on.localeCompare(a.graded_on));
  return (
    <section>
      <h2 className="text-2xl">Gradings</h2>
      {!rows.length ? (
        <p className="card mt-3 text-[15px] text-ink-500">No gradings recorded yet. Past gradings can be added by the membership secretary; reply to any EGKA email with dates if yours are missing.</p>
      ) : (
        <ul className="card mt-3 divide-y divide-stone-100 p-0">
          {rows.map(g => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-[15px]">
              <span><strong><Link to={memberLinks.person(g.whoId)} className="hover:underline">{g.who}</Link></strong> · {gradeLabel(g.for_rank)}{g.event && <> · <Link to={`/events/${g.event.slug}`} className="link">{g.event.title}</Link></>}</span>
              <span className="flex items-center gap-3 text-ink-500">{date(g.graded_on)} · <span className={g.result === 'passed' || g.recommended ? 'font-semibold text-ink-900' : ''}>{status(g)}</span>
                {g.result === 'registered' && g.graded_on >= new Date().toISOString().slice(0, 10) && <button className="btn-small" onClick={async () => { if (window.confirm('Withdraw this grading entry?')) { await supabase.rpc('withdraw_grading', { p_grading: g.id }); onChange(); } }}>Withdraw</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** A parent's say on publishing event photos of a child. Ticks at once; saves in the background. */
function PhotoConsent({ personId, firstName, value, onSaved }: { personId: string; firstName: string; value: boolean; onSaved: () => void }) {
  const [on, setOn] = useState(value);
  const [failed, setFailed] = useState(false);
  useEffect(() => setOn(value), [value]);
  return (
    <label className="mt-3 flex items-start gap-2 border-t border-stone-100 pt-3 text-sm text-ink-700">
      <input type="checkbox" className="mt-0.5 accent-brand-600" checked={on} onChange={async e => {
        const next = e.target.checked; setOn(next); setFailed(false);
        const { error } = await supabase.from('person').update({ photo_consent: next }).eq('id', personId);
        if (error) { setOn(!next); setFailed(true); } else onSaved();
      }} />
      <span>OK for EGKA to use photos of {firstName} from events on its website and social media{failed && <span className="block text-brand-700">Couldn't save that. Please try again.</span>}</span>
    </label>
  );
}

/** For a desk helper: a way in to the check-in desk of each event they are on, from two days before. */
function DeskLinks() {
  const { session } = useAuth();
  const desks = useQuery({
    queryKey: ['my-desks', session?.user.id],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase.from('event_desk').select('event:event_id(id,title,starts_at),person:person_id!inner(user_id)').eq('person.user_id', session!.user.id);
      if (error) throw error;
      const soon = Date.now() + 2 * 86_400_000, since = Date.now() - 3 * 86_400_000;
      return (data as unknown as { event: { id: string; title: string; starts_at: string } | null }[])
        .map(d => d.event).filter((e): e is NonNullable<typeof e> => !!e && new Date(e.starts_at).getTime() <= soon && new Date(e.starts_at).getTime() >= since);
    },
  });
  if (!desks.data?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {desks.data.map(e => <Link key={e.id} to={`/events/${e.id}/desk`} className="btn-primary"><ScanLine size={18} /> Check-in desk: {e.title}</Link>)}
    </div>
  );
}
